import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { cloneDeep } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { TacticCardVeevaStatusStyle, TacticChannelGroupStyle, TacticGroupBoxStyle } from './TacticCard.styles';
import { CepStatus, Channel, TacticMode, TacticTabsType, VeevaSyncStatus } from 'basics/enums/maya.enums';
import { useSetTacticStatusToCancelledMutation } from 'basics/graphql/mutations/setTacticStatusToCancelled';
import { useSetTacticStatusToDeletedMutation } from 'basics/graphql/mutations/setTacticStatusToDeleted';
import { transformTacticTitle } from 'basics/transformers/Tactic.transformer';
import { CepStateType } from 'basics/types/maya.types';
import { errorToast, succesToast, warningToast } from 'basics/utils/toast';
import { minimumAllowedNumberOfTacticsInGroup } from 'components/TacticForm/TacticForm.constants';
import useTacticValidation from 'components/TacticValidationDialog/TacticValidation.hook';
import { Tactic } from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';
import { YodaColors } from 'yoda-ui/yodaTheme';

type MenuItemConfig = {
  id: string;
  label: string;
  onClick: () => Promise<void>;
};

type UseTacticCardParams = {
  tactic: Tactic;
  allGroupedTactics: Record<string, Tactic[]> | undefined;
  isTacticGroupingImprovementsEnabled: boolean;
  onMoveToStep?: (tacticId: string, step: string, index: number) => Promise<void>;
  onCreateStepFromTacticAtStart?: (tacticId: string) => Promise<void>;
  onCreateStepFromTactic?: (tacticId: string, placeholderId: string) => Promise<void>;
  onTacticDeleted?: (deletedTacticId: string) => Promise<void>;
};

const useTacticCard = ({
  tactic,
  allGroupedTactics,
  isTacticGroupingImprovementsEnabled,
  onMoveToStep,
  onCreateStepFromTacticAtStart,
  onCreateStepFromTactic,
  onTacticDeleted,
}: UseTacticCardParams) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const [openModal, setOpenModal] = useState(false);
  const [openMoveTacticMenu, setOpenMoveTacticMenu] = useState(false);
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;
  const [setTacticStatusToDeleted, { loading: removeTacticLoading }] = useSetTacticStatusToDeletedMutation();
  const [setTacticStatusToCancelled, { loading: cancelTacticLoading }] = useSetTacticStatusToCancelledMutation();
  const { getNumberOfTacticsInGroup } = useTacticValidation();

  const isChannelMatch = (channel: Channel) => {
    return Object.keys(Channel)
      .find((key) => Channel[key as keyof typeof Channel] === channel);
  };

  const tacticGroup = useMemo(
    () => cepStateValue.tacticGroups.find((group) => group._id === tactic.parentTactic?._id) || null,
    [cepStateValue.tacticGroups, tactic.parentTactic?._id],
  );

  const currentStep = tactic?.actionOrder != null
    ? String(tactic.actionOrder)
    : null;

  const grouped = allGroupedTactics ?? {};
  const currentStepTactics = grouped[String(tactic.actionOrder)] ?? [];
  const groupedLength = Object.keys(grouped).length;
  const actionOrder = tactic.actionOrder ?? 0;

  const isLastStep = actionOrder === groupedLength;
  const hasMultipleTacticsInStep = currentStepTactics.length > 1;

  const shouldShowMoveToEnd = actionOrder < groupedLength
      || ((isLastStep && hasMultipleTacticsInStep) && tacticGroup);

  const availableStepsToMove = Object.keys(grouped)
    .filter((step) => step !== currentStep)
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  const lastStepKey = availableStepsToMove[availableStepsToMove.length - 1];

  const tacticsCount = Object.values(grouped).flat().length;
  const isOnlyTacticInOnlyStep = groupedLength === 1 && tacticsCount === 1;
  const isDisabledMovingTactic = isOnlyTacticInOnlyStep && !tacticGroup;

  const menuItemConfigs: MenuItemConfig[] = [];

  if (tactic.actionOrder !== 1 || (tactic.actionOrder === 1 && tacticGroup)) {
    menuItemConfigs.push({
      id: 'move-to-start',
      label: t('maya_tactic_card_move_to_new_start'),
      onClick: async () => {
        await onCreateStepFromTacticAtStart?.(tactic._id);
        setOpenMoveTacticMenu(false);
      },
    });
  }

  availableStepsToMove.forEach((step) => {
    if (step !== currentStep) {
      menuItemConfigs.push({
        id: step,
        label: `Step ${step}`,
        onClick: async () => {
          await onMoveToStep?.(tactic._id, step, 0);
          setOpenMoveTacticMenu(false);
        },
      });
    }
  });

  if (shouldShowMoveToEnd) {
    menuItemConfigs.push({
      id: 'move-to-end',
      label: `Step ${Object.keys(grouped).length + 1}`,
      onClick: async () => {
        await onCreateStepFromTactic?.(tactic._id, `create-step-after-${lastStepKey}`);
        setOpenMoveTacticMenu(false);
      },
    });
  }

  const getCategoryForChannel = (channel: Channel) => {
    switch (channel) {
      case isChannelMatch(Channel.approvedEmail):
      case isChannelMatch(Channel.broadcastEmail):
        return TacticTabsType.EMAIL;
      case isChannelMatch(Channel.virtualEvent):
      case isChannelMatch(Channel.f2fEvent):
      case isChannelMatch(Channel.hybridEvent):
        return TacticTabsType.EVENT;
      case isChannelMatch(Channel.f2fMeeting):
      case isChannelMatch(Channel.oneToOne):
      case isChannelMatch(Channel.oneToOneDropMaterial):
      case isChannelMatch(Channel.oneToOneIVA):
      case isChannelMatch(Channel.oneToOneKeyMessage):
      case isChannelMatch(Channel.virtualMeeting):
      case isChannelMatch(Channel.telephone):
        return TacticTabsType.INTERACTION;
      case isChannelMatch(Channel.webLogIn):
      case isChannelMatch(Channel.webNoLogIn):
        return TacticTabsType.WEB;
      default:
        return null;
    }
  };

  const getStyleFromCategory = (group: TacticTabsType | null, channel: keyof typeof Channel) => {
    switch (group) {
      case TacticTabsType.EMAIL:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: channel === 'approvedEmail'
            ? YodaColors.greenSuccessDark
            : YodaColors.green2,
        };
      case TacticTabsType.EVENT:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: YodaColors.yellow,
        };
      case TacticTabsType.INTERACTION:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: YodaColors.purple,
        };
      case TacticTabsType.WEB:
        return {
          ...TacticChannelGroupStyle,
          backgroundColor: YodaColors.primaryBlue,
        };
      default:
        return null;
    }
  };

  const generateHexColorByNumber = (number: number, contrast: number): string => {
    const r = ((number + contrast) * 100 + 50) % 256;
    const g = ((number + contrast) * 200 + 100) % 256;
    const b = ((number + contrast) * 300 + 150) % 256;

    const hexColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

    return hexColor.toUpperCase();
  };

  const getStylesFromNumberGroup = (groupNumber: number | null) => {
    const defaultContrast = 25;
    const colorNumber = groupNumber !== null ? groupNumber : 0;

    return {
      ...TacticGroupBoxStyle,
      backgroundColor: generateHexColorByNumber(colorNumber, defaultContrast),
    };
  };

  const getStyleFromVeevaSyncStatus = (status: VeevaSyncStatus) => {
    switch (status) {
      case VeevaSyncStatus.ERROR:
        return {
          ...TacticCardVeevaStatusStyle,
          color: YodaColors.error,
          backgroundColor: YodaColors.errorLight,
        };
      case VeevaSyncStatus.PENDING:
        return {
          ...TacticCardVeevaStatusStyle,
          color: YodaColors.warning,
          backgroundColor: YodaColors.warningLight,
        };
      case VeevaSyncStatus.SYNC:
        return {
          ...TacticCardVeevaStatusStyle,
          color: YodaColors.greenSuccessDark,
          backgroundColor: YodaColors.greenSuccessLight,
        };
      default:
        return null;
    }
  };

  const extractNumberFromText = (text: string | undefined): number | null => {
    if (!text) return null;

    const matches = text.match(/\d+/g);
    return matches ? parseInt(matches[matches.length - 1], 10) : null;
  };

  const editTacticCallback = () => {
    tacticState.tactic = tactic;
    tacticState.mode = TacticMode.form;
  };

  const tacticsInGroupCount = getNumberOfTacticsInGroup(tactic._id);
  const isDisabledTacticDeleting = isCepApproved && tacticsInGroupCount <= 2;

  const deleteTacticCallback = async (tacticId: string) => {
    const { data: tacticDeleted, error: tacticdDeletedError } = await setTacticStatusToDeleted(tacticId);
    if (tacticDeleted && !tacticdDeletedError) {
      if (!isTacticGroupingImprovementsEnabled) {
        succesToast(t('maya_action_deleting_tactic_success'));
        const clonedTactics = cloneDeep(cepStateValue.tactics);
        const targetTacticsIndex = clonedTactics.findIndex((element) => { return element._id === tacticId; });
        if (targetTacticsIndex !== -1) {
          clonedTactics.splice(targetTacticsIndex, 1);
          cepState.tactics = clonedTactics;
        }
      } else {
        await onTacticDeleted?.(tacticId);
      }
    } else {
      warningToast(tacticdDeletedError || t('errors_standard'));
    }

    setOpenModal(false);
  };

  const cancelTacticCallback = async (tacticId: string) => {
    const numberOfTacticsInGroup = getNumberOfTacticsInGroup(tacticId);
    const isNotCorrectTacticNumbers = numberOfTacticsInGroup > 0 && numberOfTacticsInGroup <= minimumAllowedNumberOfTacticsInGroup;
    const isApprovedCEP = cepStateValue.cep?.status === CepStatus.APPROVED;

    if (isNotCorrectTacticNumbers && flags.mayaBdcfcsd1585EnableTacticGroup && isApprovedCEP) {
      warningToast(t('maya_tactic_form_not_valid_number_of_groups_action_cancel'));
      setOpenModal(false);
      return;
    }

    const { data: tacticCancel, error: tacticCancelError } = await setTacticStatusToCancelled(tacticId);
    if (tacticCancel && !tacticCancelError) {
      if (!isTacticGroupingImprovementsEnabled) {
        succesToast(t('maya_action_canceling_tactic_success'));
        const clonedTactics = cloneDeep(cepStateValue.tactics);
        const targetTacticsIndex = clonedTactics.findIndex((element) => { return element._id === tacticId; });
        if (targetTacticsIndex !== -1) {
          clonedTactics.splice(targetTacticsIndex, 1);
          cepState.tactics = clonedTactics;
        }
      } else {
        await onTacticDeleted?.(tacticId);
      }
    } else {
      warningToast(tacticCancelError || t('errors_standard'));
    }

    setOpenModal(false);
  };

  const openModalCallback = (tacticId: string) => {
    setOpenModal(true);
    if (tacticState.tactic) {
      tacticState.tactic._id = tacticId;
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      succesToast(t('maya_successfull_copying', { text }));
    } catch (error) {
      errorToast(error as string);
    }
  };

  const getCardTitle = (tacticRecord: Tactic) => {
    const transformedTitle = transformTacticTitle(tacticRecord, flags);
    return transformedTitle.title;
  };

  return {
    deleteTacticCallback,
    cancelTacticCallback,
    editTacticCallback,
    getCategoryForChannel,
    getStyleFromCategory,
    getStyleFromVeevaSyncStatus,
    handleCopy,
    openModal,
    openModalCallback,
    removeTacticLoading,
    cancelTacticLoading,
    setOpenModal,
    extractNumberFromText,
    tacticGroup,
    getStylesFromNumberGroup,
    isCepApproved,
    getCardTitle,
    menuItemConfigs,
    isDisabledMovingTactic,
    isOnlyTacticInOnlyStep,
    isDisabledTacticDeleting,
    openMoveTacticMenu,
    setOpenMoveTacticMenu,
  };
};

export default useTacticCard;
